/**
 * Bulk Generation Service
 *
 * Handles bulk creative generation from content briefs.
 * Generates images, videos, or carousels based on brief's outputType.
 */

import { saveLibraryItem } from './libraryService';
import { updateContentBrief } from './contentPlanService';
import {
    getBrandColors,
    buildImagePrompt,
    buildVideoPrompt,
    buildCarouselNarrativeContext,
    buildCardPositionContext,
    buildReferenceImages,
    findProduct,
    findProductImageUrl,
    STORY_ARC_DEFINITIONS
} from '../../shared/promptBuilders.js';

// ============================================================================
// CONFIGURATION
// ============================================================================

const SERVER_URL = 'http://localhost:3001';

const RATE_LIMIT_CONFIG = {
    delayBetweenRequests: 15000,  // 15 seconds between requests (more conservative)
    maxRetries: 5,                 // More retry attempts
    initialRetryDelay: 30000,      // 30 seconds initial retry (allow API to recover)
    maxRetryDelay: 180000          // 3 minutes max retry
};

// ============================================================================
// PLATFORM FORMAT REQUIREMENTS
// ============================================================================

/**
 * Comprehensive platform format requirements
 * Each platform can have multiple formats with different aspect ratios
 */
const PLATFORM_FORMATS = {
    instagram: {
        feed: { aspectRatio: '1:1', name: 'Instagram Feed', description: 'Square post' },
        portrait: { aspectRatio: '4:5', name: 'Instagram Portrait', description: 'Vertical feed post' },
        stories: { aspectRatio: '9:16', name: 'Instagram Stories/Reels', description: 'Full vertical' }
    },
    facebook: {
        feed: { aspectRatio: '1:1', name: 'Facebook Feed', description: 'Square post' },
        landscape: { aspectRatio: '16:9', name: 'Facebook Landscape', description: 'Horizontal post' },
        stories: { aspectRatio: '9:16', name: 'Facebook Stories', description: 'Full vertical' }
    },
    tiktok: {
        feed: { aspectRatio: '9:16', name: 'TikTok', description: 'Full vertical video' }
    },
    youtube: {
        landscape: { aspectRatio: '16:9', name: 'YouTube', description: 'Standard horizontal' },
        shorts: { aspectRatio: '9:16', name: 'YouTube Shorts', description: 'Vertical shorts' }
    },
    twitter: {
        feed: { aspectRatio: '16:9', name: 'Twitter/X', description: 'Horizontal post' },
        square: { aspectRatio: '1:1', name: 'Twitter/X Square', description: 'Square post' }
    },
    linkedin: {
        feed: { aspectRatio: '1:1', name: 'LinkedIn Feed', description: 'Square post' },
        landscape: { aspectRatio: '1.91:1', name: 'LinkedIn Article', description: 'Article image' }
    },
    pinterest: {
        pin: { aspectRatio: '2:3', name: 'Pinterest Pin', description: 'Standard pin' }
    },
    google: {
        display: { aspectRatio: '1:1', name: 'Google Display Square', description: 'Square ad' },
        landscape: { aspectRatio: '1.91:1', name: 'Google Display Landscape', description: 'Landscape ad' },
        portrait: { aspectRatio: '9:16', name: 'Google Display Portrait', description: 'Portrait ad' }
    }
};

/**
 * Default format for each platform (used when no specific format is selected)
 */
const PLATFORM_DEFAULT_FORMAT = {
    instagram: 'feed',      // 1:1
    facebook: 'feed',       // 1:1
    tiktok: 'feed',         // 9:16
    youtube: 'landscape',   // 16:9
    twitter: 'feed',        // 16:9
    linkedin: 'feed',       // 1:1
    pinterest: 'pin',       // 2:3
    google: 'display'       // 1:1
};

/**
 * Get the primary aspect ratio for a platform (backward compatible)
 */
function getPrimaryAspectRatio(platform) {
    const platformKey = platform.toLowerCase();
    const defaultFormat = PLATFORM_DEFAULT_FORMAT[platformKey] || 'feed';
    return PLATFORM_FORMATS[platformKey]?.[defaultFormat]?.aspectRatio || '1:1';
}

/**
 * Get optimal aspect ratio based on target platforms (backward compatible)
 * Returns the primary aspect ratio for the first platform
 */
function getAspectRatioForPlatforms(platforms = []) {
    if (!platforms || platforms.length === 0) {
        return '1:1'; // Default to square
    }
    return getPrimaryAspectRatio(platforms[0]);
}

/**
 * Get all unique aspect ratios required for the selected platforms
 * Returns array of { aspectRatio, platforms, formatName }
 */
function getAllRequiredAspectRatios(platforms = []) {
    if (!platforms || platforms.length === 0) {
        return [{ aspectRatio: '1:1', platforms: ['default'], formatName: 'Square' }];
    }

    const aspectRatioMap = new Map(); // aspectRatio -> { platforms: [], formatName }

    for (const platform of platforms) {
        const platformKey = platform.toLowerCase();
        const formats = PLATFORM_FORMATS[platformKey];

        if (!formats) continue;

        // Get all formats for this platform
        for (const [formatKey, formatData] of Object.entries(formats)) {
            const ar = formatData.aspectRatio;
            if (!aspectRatioMap.has(ar)) {
                aspectRatioMap.set(ar, {
                    platforms: [],
                    formatNames: []
                });
            }
            const entry = aspectRatioMap.get(ar);
            if (!entry.platforms.includes(platformKey)) {
                entry.platforms.push(platformKey);
            }
            entry.formatNames.push(formatData.name);
        }
    }

    // Convert to array and deduplicate format names
    return Array.from(aspectRatioMap.entries()).map(([aspectRatio, data]) => ({
        aspectRatio,
        platforms: data.platforms,
        formatName: [...new Set(data.formatNames)].join(', ')
    }));
}

/**
 * Get required aspect ratios for platforms, but only unique ones
 * This is used when we want to minimize generation while covering all platforms
 */
function getUniqueRequiredAspectRatios(platforms = []) {
    const allRequired = getAllRequiredAspectRatios(platforms);

    // Deduplicate by aspect ratio
    const seen = new Set();
    return allRequired.filter(item => {
        if (seen.has(item.aspectRatio)) {
            return false;
        }
        seen.add(item.aspectRatio);
        return true;
    });
}

// findProduct and findProductImageUrl imported from shared/promptBuilders.js

// ============================================================================
// UTILITIES
// ============================================================================

function delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

async function fetchWithRetry(url, options, retryCount = 0, onRetry = null) {
    try {
        const response = await fetch(url, options);

        if (response.status === 429 || response.status === 503 || response.status === 422) {
            if (retryCount >= RATE_LIMIT_CONFIG.maxRetries) {
                throw new Error('Rate limit exceeded after maximum retries. Please try again later.');
            }

            const retryDelay = Math.min(
                RATE_LIMIT_CONFIG.initialRetryDelay * Math.pow(2, retryCount),
                RATE_LIMIT_CONFIG.maxRetryDelay
            );

            const retryMessage = `Rate limited. Retrying in ${Math.round(retryDelay / 1000)}s... (attempt ${retryCount + 1}/${RATE_LIMIT_CONFIG.maxRetries})`;
            console.log(retryMessage);

            // Notify caller about retry
            onRetry?.({
                type: 'rate_limit',
                attempt: retryCount + 1,
                maxAttempts: RATE_LIMIT_CONFIG.maxRetries,
                retryInSeconds: Math.round(retryDelay / 1000),
                message: retryMessage
            });

            await delay(retryDelay);

            return fetchWithRetry(url, options, retryCount + 1, onRetry);
        }

        return response;
    } catch (error) {
        if (error.message.includes('Rate limit exceeded')) {
            throw error;
        }

        if (retryCount < RATE_LIMIT_CONFIG.maxRetries) {
            const retryDelay = RATE_LIMIT_CONFIG.initialRetryDelay * Math.pow(2, retryCount);
            const retryMessage = `Network error. Retrying in ${Math.round(retryDelay / 1000)}s...`;
            console.log(retryMessage);

            onRetry?.({
                type: 'network_error',
                attempt: retryCount + 1,
                maxAttempts: RATE_LIMIT_CONFIG.maxRetries,
                retryInSeconds: Math.round(retryDelay / 1000),
                message: retryMessage
            });

            await delay(retryDelay);
            return fetchWithRetry(url, options, retryCount + 1, onRetry);
        }

        throw error;
    }
}

// ============================================================================
// PROMPT BUILDERS
// ============================================================================

// getBrandColors imported from shared/promptBuilders.js

// buildImagePrompt imported from shared/promptBuilders.js

// buildVideoPrompt imported from shared/promptBuilders.js

// ============================================================================
// GENERATION FUNCTIONS
// ============================================================================

/**
 * Generate an image from a brief
 * @param {Object} brief - The content brief
 * @param {Object} brand - Brand information
 * @param {Function} onRetry - Retry callback
 * @param {Object} options - Additional options { referenceImages, aspectRatio, product }
 */
async function generateImage(brief, brand, onRetry = null, options = {}) {
    const { referenceImages = [], aspectRatio = '1:1', product = null } = options;
    const prompt = buildImagePrompt(brief, brand, product);

    // Build reference images array for the API
    const formattedReferenceImages = referenceImages
        .filter(img => img?.url)
        .map(img => ({
            url: img.url,
            instruction: img.instruction || 'Use this product image as visual reference for the creative.'
        }));

    // Use Gemini endpoint (API key auth) instead of Vertex AI (requires Google Cloud auth)
    const response = await fetchWithRetry(`${SERVER_URL}/api/generate-image-gemini`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            prompt,
            userId: brief.userId, // For billing/credits
            aspectRatio,
            referenceImages: formattedReferenceImages.length > 0 ? formattedReferenceImages : undefined
        })
    }, 0, onRetry);

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.details || error.error || error.message || 'Image generation failed');
    }

    const data = await response.json();
    return {
        type: 'image',
        url: data.url,
        prompt: prompt, // Always use the local prompt to ensure it's never undefined
        generatedAt: data.generatedAt || new Date().toISOString()
    };
}

/**
 * Generate a video from a brief
 */
async function generateVideo(brief, brand, onRetry = null) {
    const prompt = buildVideoPrompt(brief, brand);
    const briefData = brief.brief || brief;
    const videoSettings = briefData.videoSettings || {};

    // Start video generation
    const startResponse = await fetchWithRetry(`${SERVER_URL}/api/generate-video`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            prompt,
            duration: videoSettings.videoLength || '6s',
            style: videoSettings.videoStyle || 'commercial',
            aspectRatio: '9:16'
        })
    }, 0, onRetry);

    if (!startResponse.ok) {
        const error = await startResponse.json();
        throw new Error(error.details || error.error || 'Video generation failed to start');
    }

    const startData = await startResponse.json();
    const operationId = startData.operationId || startData.id;

    if (!operationId) {
        // Video was generated synchronously
        return {
            type: 'video',
            url: startData.url || startData.videoUrl,
            thumbnailUrl: startData.thumbnailUrl,
            prompt,
            duration: videoSettings.videoLength || '6s',
            generatedAt: new Date().toISOString()
        };
    }

    // Poll for completion
    let attempts = 0;
    const maxAttempts = 60; // 5 minutes max (5s intervals)

    while (attempts < maxAttempts) {
        await delay(5000); // Wait 5 seconds between polls

        const statusResponse = await fetch(`${SERVER_URL}/api/video-status/${operationId}`);
        if (!statusResponse.ok) {
            attempts++;
            continue;
        }

        const statusData = await statusResponse.json();

        if (statusData.status === 'complete' || statusData.status === 'completed') {
            return {
                type: 'video',
                url: statusData.url || statusData.videoUrl,
                thumbnailUrl: statusData.thumbnailUrl,
                prompt,
                duration: videoSettings.videoLength || '6s',
                generatedAt: new Date().toISOString()
            };
        }

        if (statusData.status === 'failed' || statusData.status === 'error') {
            throw new Error(statusData.error || 'Video generation failed');
        }

        attempts++;
    }

    throw new Error('Video generation timed out');
}

// STORY_ARC_DEFINITIONS, buildCarouselNarrativeContext, and buildCardPositionContext
// imported from shared/promptBuilders.js

/**
 * Generate carousel images from a brief
 * @param {Object} brief - The content brief
 * @param {Object} brand - Brand information
 * @param {Function} onRetry - Retry callback
 * @param {Object} options - Additional options { referenceImages, aspectRatio }
 */
async function generateCarousel(brief, brand, onRetry = null, options = {}) {
    const briefData = brief.brief || brief;
    const cardCount = briefData.carouselCards?.length || 4;
    const { referenceImages = [], aspectRatio = '1:1', product = null } = options;

    // Build reference images array for the API
    const allFormattedReferenceImages = referenceImages
        .filter(img => img?.url)
        .map(img => ({
            url: img.url,
            instruction: img.instruction || 'Use this product image as visual reference for the creative.'
        }));

    // For card 1 (hook), exclude the logo reference image
    const noLogoReferenceImages = allFormattedReferenceImages.filter(
        img => !img.instruction?.includes('BRAND LOGO')
    );

    // Build pillar context from the outer brief object (available for all cards)
    let carouselPillarContext = '';
    const pillarName = brief.pillarName || '';
    const pillarDescription = brief.pillarDescription || '';
    const pillarGuidelines = brief.pillarGuidelines || {};
    if (pillarName) {
        const pillarTone = pillarGuidelines.toneOfVoice?.length > 0 ? pillarGuidelines.toneOfVoice.join(', ') : '';
        const pillarMessages = pillarGuidelines.keyMessages?.length > 0 ? pillarGuidelines.keyMessages.join('; ') : '';
        carouselPillarContext = `CONTENT PILLAR: "${pillarName}"${pillarDescription ? ` - ${pillarDescription}` : ''}`;
        if (pillarTone) carouselPillarContext += `\nPILLAR TONE OF VOICE: ${pillarTone}`;
        if (pillarMessages) carouselPillarContext += `\nKEY MESSAGES TO REINFORCE: ${pillarMessages}`;
    }

    const images = [];

    // Build full narrative context BEFORE the card loop so every card knows the full story
    const { contextBlock, arcInfo } = buildCarouselNarrativeContext(briefData);

    console.log('🎠 [CAROUSEL-V2] Generating carousel with narrative-linked card prompts...');

    for (let i = 0; i < cardCount; i++) {
        const card = briefData.carouselCards?.[i];

        // Check if card has its own scene description from brief generation
        const hasCardScene = card?.sceneDescription && card.sceneDescription !== briefData.sceneDescription;
        console.log(`🎠 Card ${i + 1}: hasCardScene=${hasCardScene}, cardScene="${card?.sceneDescription?.substring(0, 50) || 'none'}"`);

        // Determine logo inclusion: card 1 = no logo, last card = required, middle = optional
        const isFirstCard = i === 0;
        const isLastCard = i === cardCount - 1;
        const cardIncludeLogo = !isFirstCard;

        // Build logo instruction for the prompt
        let logoGuidance = '';
        if (isFirstCard) {
            logoGuidance = '\n\nLOGO: Do NOT include any brand logo on this card.';
        } else if (isLastCard) {
            logoGuidance = `\n\nLOGO: Brand logo MUST be prominently displayed on this card (bottom corner or center).`;
        } else {
            logoGuidance = '\n\nLOGO: Brand logo optional - include only if it fits the composition naturally.';
        }

        // Build narrative-aware position context using the actual story arc and sibling cards
        const cardPositionContext = buildCardPositionContext(i, briefData.carouselCards || [], arcInfo);

        // Build arc-driven scene modification (only when card lacks its own sceneDescription)
        let sceneModification = '';
        if (!hasCardScene) {
            const role = card?.cardRole || (isFirstCard ? 'hook' : isLastCard ? 'cta' : 'build');
            const roleGuidance = arcInfo.cardRoleGuide[role] || arcInfo.cardRoleGuide.support;
            sceneModification = `\n\nSCENE DIRECTION FOR THIS CARD (${briefData.storyArcType || 'problem_solution'} arc, ${role} role): ${roleGuidance}`;
        }

        // Build card-specific data - use card's scene if available, otherwise use base with modifications
        // Map card fields to the names buildImagePrompt() expects for text overlays:
        //   card.headline → selectedHeadline (for TEXT OVERLAY - HEADLINE)
        //   card.ctaButtonText → cta (for TEXT OVERLAY - CTA)
        let cardData;
        if (hasCardScene) {
            cardData = { ...briefData, ...card };
        } else if (card) {
            cardData = { ...briefData, ...card };
        } else {
            cardData = { ...briefData };
        }
        if (card?.headline) {
            cardData.selectedHeadline = card.headline;
        }
        if (card?.ctaButtonText) {
            cardData.cta = card.ctaButtonText;
        }

        // Build the prompt: base image prompt + full narrative context + card position context + scene direction + logo
        const cardPrompt = buildImagePrompt({ brief: cardData }, brand, product, { includeLogo: cardIncludeLogo, pillarContext: carouselPillarContext })
            + '\n' + contextBlock
            + '\n' + cardPositionContext
            + sceneModification
            + logoGuidance;
        console.log(`🎠 Card ${i + 1} role: ${card?.cardRole || 'unknown'}, Logo: ${isFirstCard ? 'NO' : isLastCard ? 'REQUIRED' : 'OPTIONAL'}`);

        // Card 1 (hook): no logo reference. All others: include logo reference.
        const cardRefImages = isFirstCard ? noLogoReferenceImages : allFormattedReferenceImages;

        // Use Gemini endpoint (API key auth) instead of Vertex AI
        const response = await fetchWithRetry(`${SERVER_URL}/api/generate-image-gemini`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                prompt: cardPrompt,
                userId: brief.userId, // For billing/credits
                aspectRatio, // Use platform-specific aspect ratio
                referenceImages: cardRefImages.length > 0 ? cardRefImages : undefined
            })
        }, 0, onRetry);

        if (!response.ok) {
            const error = await response.json();
            throw new Error(`Carousel card ${i + 1} generation failed: ${error.details || error.error || error.message}`);
        }

        const data = await response.json();
        images.push({
            index: i,
            url: data.url,
            headline: card?.headline || briefData.selectedHeadline,
            caption: card?.caption || briefData.caption || '',
            subtext: card?.subtext
        });

        // Delay between carousel cards
        if (i < cardCount - 1) {
            await delay(RATE_LIMIT_CONFIG.delayBetweenRequests);
        }
    }

    return {
        type: 'carousel',
        images,
        prompt: buildImagePrompt(brief, brand, product), // Include prompt for metadata
        generatedAt: new Date().toISOString()
    };
}

// ============================================================================
// MAIN EXPORT FUNCTIONS
// ============================================================================

/**
 * Generate a creative from a single brief
 * @param {Object} brief - The content brief
 * @param {Object} brand - Brand information
 * @param {Function} onRetry - Retry callback
 * @param {Object} options - Additional options { referenceImages, aspectRatio }
 */
export async function generateCreativeFromBrief(brief, brand, onRetry = null, options = {}) {
    const outputType = brief.outputType || 'image';

    switch (outputType) {
        case 'video':
            return generateVideo(brief, brand, onRetry);
        case 'carousel':
            return generateCarousel(brief, brand, onRetry, options);
        case 'image':
        default:
            return generateImage(brief, brand, onRetry, options);
    }
}

/**
 * Generate multiple size variants for different platforms
 * @param {Object} brief - The content brief
 * @param {Object} brand - Brand information
 * @param {Function} onRetry - Retry callback
 * @param {Object} options - Additional options { referenceImages, aspectRatios, product }
 * @param {Function} onVariantProgress - Progress callback for variant generation
 * @returns {Object} - { primary: result, variants: [{ aspectRatio, platforms, formatName, result }] }
 */
async function generateMultiSizeVariants(brief, brand, onRetry = null, options = {}, onVariantProgress = null) {
    const { referenceImages = [], aspectRatios = [], product = null } = options;
    const outputType = brief.outputType || 'image';

    // Videos always use 9:16, don't need variants
    if (outputType === 'video') {
        const result = await generateVideo(brief, brand, onRetry);
        return {
            primary: result,
            variants: [],
            primaryAspectRatio: '9:16'
        };
    }

    // If no aspect ratios specified or only one, generate single image
    if (aspectRatios.length <= 1) {
        const aspectRatio = aspectRatios[0]?.aspectRatio || '1:1';
        const result = outputType === 'carousel'
            ? await generateCarousel(brief, brand, onRetry, { referenceImages, aspectRatio, product })
            : await generateImage(brief, brand, onRetry, { referenceImages, aspectRatio, product });

        return {
            primary: result,
            variants: [],
            primaryAspectRatio: aspectRatio
        };
    }

    // Generate primary (first aspect ratio) and variants for remaining
    const [primaryAR, ...variantARs] = aspectRatios;
    const variants = [];

    // Generate primary
    onVariantProgress?.({
        phase: 'variant',
        current: 1,
        total: aspectRatios.length,
        aspectRatio: primaryAR.aspectRatio,
        formatName: primaryAR.formatName
    });

    const primaryResult = outputType === 'carousel'
        ? await generateCarousel(brief, brand, onRetry, { referenceImages, aspectRatio: primaryAR.aspectRatio, product })
        : await generateImage(brief, brand, onRetry, { referenceImages, aspectRatio: primaryAR.aspectRatio, product });

    // Generate variants
    for (let i = 0; i < variantARs.length; i++) {
        const variantAR = variantARs[i];

        onVariantProgress?.({
            phase: 'variant',
            current: i + 2,
            total: aspectRatios.length,
            aspectRatio: variantAR.aspectRatio,
            formatName: variantAR.formatName
        });

        // Delay between variant requests
        await delay(RATE_LIMIT_CONFIG.delayBetweenRequests);

        try {
            const variantResult = outputType === 'carousel'
                ? await generateCarousel(brief, brand, onRetry, { referenceImages, aspectRatio: variantAR.aspectRatio, product })
                : await generateImage(brief, brand, onRetry, { referenceImages, aspectRatio: variantAR.aspectRatio, product });

            variants.push({
                aspectRatio: variantAR.aspectRatio,
                platforms: variantAR.platforms,
                formatName: variantAR.formatName,
                result: variantResult
            });
        } catch (error) {
            console.error(`Failed to generate variant ${variantAR.aspectRatio}:`, error);
            // Continue with other variants even if one fails
            variants.push({
                aspectRatio: variantAR.aspectRatio,
                platforms: variantAR.platforms,
                formatName: variantAR.formatName,
                error: error.message
            });
        }
    }

    return {
        primary: primaryResult,
        primaryAspectRatio: primaryAR.aspectRatio,
        primaryPlatforms: primaryAR.platforms,
        primaryFormatName: primaryAR.formatName,
        variants
    };
}

/**
 * Process bulk generation for multiple briefs
 *
 * @param {Array} briefs - Array of approved briefs to generate
 * @param {Object} brand - Brand information
 * @param {Function} onProgress - Progress callback ({ current, total, phase, brief, status })
 * @param {Object} generationOptions - Additional options { products, targetPlatforms, generateAllSizes }
 * @returns {Array} - Array of generation results
 */
export async function processBulkGeneration(briefs, brand, onProgress, generationOptions = {}) {
    const results = [];
    const total = briefs.length;
    const { products = [], targetPlatforms = [], generateAllSizes = true } = generationOptions;

    // Get all required aspect ratios for the selected platforms
    const requiredAspectRatios = generateAllSizes
        ? getUniqueRequiredAspectRatios(targetPlatforms)
        : [{ aspectRatio: getAspectRatioForPlatforms(targetPlatforms), platforms: targetPlatforms, formatName: 'Primary' }];

    console.log(`Generating for ${requiredAspectRatios.length} aspect ratio(s):`, requiredAspectRatios.map(ar => ar.aspectRatio));

    for (let i = 0; i < briefs.length; i++) {
        const brief = briefs[i];

        // Update progress - starting
        onProgress?.({
            current: i,
            total,
            phase: 'generating',
            brief,
            status: 'in_progress',
            variantCount: requiredAspectRatios.length
        });

        try {
            // Add delay between requests (except first)
            if (i > 0) {
                const waitSeconds = Math.round(RATE_LIMIT_CONFIG.delayBetweenRequests / 1000);
                onProgress?.({
                    current: i,
                    total,
                    phase: 'waiting',
                    message: `Waiting ${waitSeconds}s before next request...`,
                    waitSeconds
                });
                await delay(RATE_LIMIT_CONFIG.delayBetweenRequests);
            }

            // Create retry handler that updates progress
            const onRetry = (retryInfo) => {
                onProgress?.({
                    current: i,
                    total,
                    phase: 'retrying',
                    brief,
                    status: 'retrying',
                    retry: retryInfo
                });
            };

            // Create variant progress handler
            const onVariantProgress = (variantInfo) => {
                onProgress?.({
                    current: i,
                    total,
                    phase: 'generating_variant',
                    brief,
                    status: 'in_progress',
                    variant: variantInfo
                });
            };

            // Find product for reference (using unified findProduct that handles both name and brief)
            const briefData = brief.brief || brief;
            const productName = briefData.product;
            const product = findProduct(brief, products);

            // Build reference images (logo + product) using shared builder
            const referenceImages = buildReferenceImages(brand, product, brief);
            console.log(`[Generation] Product: ${productName}, Reference images: ${referenceImages.length}`);

            // Generate the creative with all required size variants
            const multiSizeResult = await generateMultiSizeVariants(
                brief,
                brand,
                onRetry,
                { referenceImages, aspectRatios: requiredAspectRatios, product },
                onVariantProgress
            );

            const { primary: result, variants, primaryAspectRatio } = multiSizeResult;

            // Save to library - ensure no undefined values (Firestore rejects undefined)
            const imageUrl = result.type === 'carousel' ? result.images[0]?.url : result.url;
            const promptForMetadata = result.prompt || buildImagePrompt(brief, brand, product) || 'No prompt available';

            // Build size variants array for storage
            const sizeVariants = variants
                .filter(v => !v.error && v.result)
                .map(v => ({
                    aspectRatio: v.aspectRatio,
                    platforms: v.platforms,
                    formatName: v.formatName,
                    imageUrl: v.result.type === 'carousel' ? v.result.images[0]?.url : v.result.url,
                    thumbnailUrl: v.result.thumbnailUrl || (v.result.type === 'carousel' ? v.result.images[0]?.url : v.result.url),
                    generatedAt: v.result.generatedAt || new Date().toISOString(),
                    ...(v.result.type === 'carousel' && { carouselImages: v.result.images || [] })
                }));

            const libraryItemId = await saveLibraryItem({
                name: briefData.selectedHeadline || brief.brief?.selectedHeadline || `Brief #${brief.sequence || 'unknown'}`,
                type: result.type || 'image',
                imageUrl: imageUrl || null,
                thumbnailUrl: result.thumbnailUrl || imageUrl || null,
                description: briefData.caption || briefData.description || null,
                tags: briefData.hashtags || [],
                // Paid content fields for scheduling
                cta: briefData.cta || null,
                productPageUrl: briefData.productPageUrl || null,
                productId: briefData.productId || null,
                contentType: brief.contentType || 'organic', // organic | paid
                pillarId: brief.pillarId || null,
                pillarName: brief.pillarName || null,
                pillarColor: brief.pillarColor || null,
                metadata: {
                    briefId: brief.id || null,
                    planId: brief.planId || null,
                    pillarName: brief.pillarName || null,
                    funnelStage: brief.funnelStage || null,
                    prompt: promptForMetadata,
                    outputType: brief.outputType || 'image',
                    generatedAt: result.generatedAt || new Date().toISOString(),
                    aspectRatio: primaryAspectRatio || '1:1',
                    productName: productName || null,
                    caption: briefData.caption || null,
                    hashtags: briefData.hashtags || null,
                    cta: briefData.cta || null,
                    productPageUrl: briefData.productPageUrl || null,
                    targetPlatforms: targetPlatforms || [],
                    // Store size variants for multi-platform support
                    sizeVariants: sizeVariants.length > 0 ? sizeVariants : null,
                    ...(result.type === 'carousel' && { carouselImages: result.images || [] })
                }
            }, brief.userId, brief.brandId);

            // Update brief with generated creative ID and image URL
            // Skip for synthetic A/B variant IDs (they contain '_variant' and aren't in Firestore)
            if (brief.id && !brief.id.includes('_variant')) {
                await updateContentBrief(brief.id, {
                    status: 'generated',
                    generatedCreativeId: libraryItemId,
                    generatedImageUrl: imageUrl,
                    generatedThumbnailUrl: result.thumbnailUrl || imageUrl,
                    generatedAt: result.generatedAt,
                    generatedAspectRatio: primaryAspectRatio,
                    generatedVariantCount: sizeVariants.length + 1, // Primary + variants
                    ...(result.type === 'carousel' && { carouselImages: result.images })
                });
            }

            results.push({
                briefId: brief.id,
                success: true,
                result,
                variants: sizeVariants,
                libraryItemId,
                imageUrl,
                thumbnailUrl: result.thumbnailUrl || imageUrl
            });

            // Update progress - success
            onProgress?.({
                current: i + 1,
                total,
                phase: 'complete',
                brief,
                status: 'success',
                imageUrl,
                thumbnailUrl: result.thumbnailUrl || imageUrl,
                libraryItemId,
                variantCount: sizeVariants.length + 1,
                outputType: result.type || 'image',
                carouselImages: result.type === 'carousel' ? result.images : null
            });

        } catch (error) {
            console.error(`Generation failed for brief ${brief.id}:`, error);

            results.push({
                briefId: brief.id,
                success: false,
                error: error.message
            });

            // Update progress - failed
            onProgress?.({
                current: i + 1,
                total,
                phase: 'complete',
                brief,
                status: 'failed',
                error: error.message
            });
        }
    }

    return results;
}

/**
 * Retry failed generations
 */
export async function retryFailedGenerations(failedBriefs, brand, onProgress, generationOptions = {}) {
    return processBulkGeneration(failedBriefs, brand, onProgress, generationOptions);
}

// Export platform format constants for use in UI
export { PLATFORM_FORMATS, PLATFORM_DEFAULT_FORMAT };
export { getUniqueRequiredAspectRatios, getAllRequiredAspectRatios };

export default {
    generateCreativeFromBrief,
    processBulkGeneration,
    retryFailedGenerations,
    PLATFORM_FORMATS,
    PLATFORM_DEFAULT_FORMAT,
    getUniqueRequiredAspectRatios,
    getAllRequiredAspectRatios
};
